import React from "react";
import { Easing, interpolate, useCurrentFrame } from "remotion";
import { fill, useCopy } from "../copy";
import { LOOK } from "../look";
import { useStage } from "../stage";
import { useTiming } from "../timing";
import { Headline, Kicker, Split, Sub } from "../ui/Split";
import { Sfx } from "../ui/Sfx";
import { Avatar, usePop, Window } from "../ui/Surfaces";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Step 3 — every sale is attributed, every ambassador paid automatically.
 * The partner page's "suivi des performances" card, coded and live: each
 * recommended order drops into the feed, ticks the KPIs up and pays the
 * 8 € set in step 1, while the CAC tile stays put at 10 €. Sample data,
 * and labelled as such ("Exemple" in the window bar).
 */

const VISUAL = { w: 860, h: 700 };
const CAC = 10;
const GAIN = 8;
const BASE_SALES = 125;
const BASE_REVENUE = 11240;

type Sale = { name: string; img: string; order: number; at: number };
/**
 * One sale every other beat (scene beats 2 → 10), notification then
 * payout, so the dashboard stays live through the whole hold. The feed
 * shows the newest `ROWS`; the oldest fades out as a new one lands.
 */
const SALES = [
  { name: "Léa", img: "b2b/people/center.webp", order: 89, beat: 2 },
  { name: "Karim", img: "b2b/people/top-left.webp", order: 64, beat: 4 },
  { name: "Julie", img: "b2b/people/mid-left.webp", order: 120, beat: 6 },
  { name: "Inès", img: "b2b/people/bottom-right.webp", order: 75, beat: 8 },
  { name: "Hugo", img: "b2b/people/top-mid.webp", order: 58, beat: 10 },
];
const ROWS = 3;
/** Frames from the sale landing to its "paid" badge. */
const PAID_AFTER = 12;

const useSales = (): Sale[] => {
  const { beat } = useTiming();
  return SALES.map((s) => ({ ...s, at: Math.round(s.beat * beat) }));
};

/** Value that steps up by `step(i)` as each sale lands (eased). */
const useTicker = (sales: Sale[], base: number, step: (s: Sale) => number) => {
  const frame = useCurrentFrame();
  return sales.reduce(
    (sum, s) =>
      sum +
      step(s) *
        interpolate(frame, [s.at + 4, s.at + 14], [0, 1], {
          ...clamp,
          easing: Easing.bezier(0.16, 1, 0.3, 1),
        }),
    base,
  );
};

const Kpi: React.FC<{
  label: string;
  value: string;
  tone?: string;
  badge?: string;
  at: number;
}> = ({ label, value, tone = LOOK.ink, badge, at }) => {
  const p = usePop(at, true);
  return (
    <div
      style={{
        flex: 1,
        padding: "22px 24px",
        borderRadius: 26,
        background: LOOK.mist,
        opacity: p,
        translate: `0 ${(1 - p) * 20}px`,
      }}
    >
      <div
        style={{
          fontFamily: LOOK.body,
          fontWeight: LOOK.medium,
          fontSize: 22,
          color: LOOK.inkSoft,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          marginTop: 6,
        }}
      >
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 46,
            letterSpacing: -1.5,
            color: tone,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </div>
        {badge ? (
          <div
            style={{
              padding: "3px 12px",
              borderRadius: 999,
              background: LOOK.white,
              fontFamily: LOOK.body,
              fontWeight: LOOK.semi,
              fontSize: 20,
              color: LOOK.blue,
            }}
          >
            {badge}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const Row: React.FC<{ sale: Sale; leaveAt: number }> = ({ sale, leaveAt }) => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const t = copy.track;
  const p = usePop(sale.at, true);
  const leave = interpolate(frame, [leaveAt - 5, leaveAt - 1], [1, 0], clamp);
  const paid = usePop(sale.at + PAID_AFTER);
  // Fresh row glows briefly.
  const glow = interpolate(
    frame,
    [sale.at, sale.at + 6, sale.at + 26],
    [0, 1, 0],
    clamp,
  );
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 20,
        padding: "16px 20px",
        borderRadius: 24,
        background: `rgba(229, 236, 253, ${glow})`,
        opacity: p * leave,
        translate: `0 ${(1 - p) * -30}px`,
      }}
    >
      <Avatar size={68} src={sale.img} />
      <div style={{ flex: 1, lineHeight: 1.2 }}>
        <div
          style={{
            fontFamily: LOOK.body,
            fontWeight: LOOK.semi,
            fontSize: 30,
            color: LOOK.ink,
          }}
        >
          {fill(t.recommended, { name: sale.name })}
        </div>
        <div
          style={{ fontFamily: LOOK.body, fontSize: 24, color: LOOK.inkSoft }}
        >
          {fill(t.order, { amount: copy.money(sale.order) })}
        </div>
      </div>
      <div
        style={{
          padding: "10px 20px",
          borderRadius: 999,
          background: LOOK.greenSoft,
          color: LOOK.green,
          fontFamily: LOOK.body,
          fontWeight: LOOK.bold,
          fontSize: 26,
          whiteSpace: "nowrap",
          scale: String(paid),
        }}
      >
        ✓ {fill(t.paid, { gain: copy.money(GAIN) })}
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const t = copy.track;
  const list = useSales();
  const sales = useTicker(list, BASE_SALES, () => 1);
  const revenue = useTicker(list, BASE_REVENUE, (s) => s.order);
  const liveDot = 0.5 + 0.5 * Math.sin(frame / 5);
  // Newest first; a row leaves when the sale `ROWS` later lands.
  const leaveAt = (i: number) => list[i + ROWS]?.at ?? 1e9;
  const visible = list
    .map((s, i) => ({ s, leaveAt: leaveAt(i) }))
    .filter(({ s, leaveAt: out }) => frame >= s.at - 1 && frame < out - 1)
    .reverse();

  return (
    <Window
      tag={copy.sample}
      style={{ width: VISUAL.w, height: VISUAL.h }}
      bodyStyle={{ padding: "34px 40px" }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 40,
            color: LOOK.ink,
            letterSpacing: -1,
          }}
        >
          {t.title}
        </div>
        <div
          style={{
            marginLeft: 18,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: LOOK.body,
            fontWeight: LOOK.semi,
            fontSize: 22,
            color: LOOK.green,
          }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 12,
              background: LOOK.green,
              opacity: liveDot,
            }}
          />
          {t.live}
        </div>
      </div>

      <div style={{ display: "flex", gap: 16 }}>
        <Kpi at={0} label={t.sales} value={copy.int(sales)} />
        <Kpi at={3} label={t.revenue} value={copy.money(revenue)} />
        <Kpi
          at={6}
          label={t.cac}
          value={copy.money(CAC)}
          tone={LOOK.blue}
          badge={t.fixed}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginTop: 24,
        }}
      >
        {visible.map(({ s, leaveAt: out }) => (
          <Row key={s.name} sale={s} leaveAt={out} />
        ))}
      </div>
    </Window>
  );
};

export const Track: React.FC = () => {
  const copy = useCopy();
  const stage = useStage();
  const sales = useSales();
  return (
    <>
      <Split
        background={LOOK.sky}
        visualSize={VISUAL}
        // Square: less room for text, more for the dashboard (see SetCac).
        textH={{ tall: 390, square: 310, wide: 400 }[stage.shape]}
        text={
          <>
            <Kicker text={copy.track.kicker} step={3} />
            <Headline text={copy.track.headline} start={2} />
            <Sub text={copy.track.sub} at={30} />
          </>
        }
        visual={<Dashboard />}
      />
      {sales.map((s) => (
        <React.Fragment key={s.name}>
          <Sfx sound="notify" at={s.at} />
          <Sfx sound="coins" at={s.at + PAID_AFTER} />
        </React.Fragment>
      ))}
    </>
  );
};
